import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { heatRamp } from '~/configs/unifiedAnalyticsTheme';

import { Box, Typography } from '@mui/material';
import { type Theme } from '@mui/material/styles';

import { BRAND_COLORS } from '~/components/brand/brandTokens';

import type { LensCard, LensCardConfig, LensResolvedPeriod } from '~/types/leLens';

import { LensCalendarContext } from '../LensCalendarContext';
import { canFitZoomControl } from '../cardLayout';
import { type LensMarkPick, type LensSelection, buildPieModel } from '../cardView';
import {
  OTHER_SERIES,
  buildChartModel,
  cardChartConfig,
  dailyComparisonChartModel,
  displayLensFieldName,
  formatLensDateCategoryLabel,
  isNumeric,
  partialPeriodIndex,
  prepareLensTimeAxis,
  rankedCategoryRows,
  seriesColors,
  shouldShowDirectLabels,
} from '../lensChart';
import {
  type LensEChartsCrossFilter,
  type LensEChartsOption,
  type LensEChartsTheme,
  buildLensEChartsBoxplotOption,
  buildLensEChartsHeatmapOption,
  buildLensEChartsOption,
  buildLensEChartsPieOption,
  buildLensEChartsScatterOption,
} from '../lensEChartsOption';
import { buildDistributions, buildMatrixModel, distributionExtent } from '../lensMatrix';
import { partialPeriodNote } from '../lensTime';

import LensCrossFilterKeys, { type LensCrossFilterKey } from './LensCrossFilterKeys';
import LensEChart, { type LensChartHandle, type LensEChartClick } from './LensEChart';

const NONE_LABEL = 'None';
/** Past this many marks the x axis stops being readable without a zoom control. */
const ZOOM_THRESHOLD = 60;
/** Card types whose option reads a cross-filter's highlight colour and marker.
 *  Wider than the bar/stacked-only click-to-select gate further down, which
 *  governs which types can raise a selection rather than reflect one. */
const CROSS_FILTER_HIGHLIGHT_TYPES: ReadonlyArray<LensCard['type']> = ['bar', 'stacked', 'line', 'area', 'combo'];

type Props = {
  card: LensCard;
  shaped: { rows: Array<Record<string, unknown>>; columns: Array<string> };
  comparison?: {
    shaped: { rows: Array<Record<string, unknown>>; columns: Array<string> };
    period?: LensResolvedPeriod;
  };
  plotHeight: number;
  selection?: LensSelection;
  onSelect: (pick: LensMarkPick) => void;
  theme: Theme;
  truncated: boolean;
  /** Lets the card menu ask the live instance for a picture of itself. */
  chartRef?: React.Ref<LensChartHandle>;
  /** Saves an order a reader set by dragging one category past another. */
  onReorderCategories?: (order: Array<string>) => void;
  /**
   * The card's rows before ranges and cross-filter selections narrow them,
   * for a stable series-to-colour assignment: colour follows the entity,
   * never its rank, so a series must keep its palette slot even when a
   * narrowing drops a neighbour that sat alphabetically between it and
   * another. Omitted falls back to ranking only the series in `shaped`,
   * which is the whole set for a card no filter has touched yet.
   */
  unfilteredShaped?: { rows: Array<Record<string, unknown>>; columns: Array<string> };
};

/** Recessive grid and axis rule colour, per the brand guide: never the admin
 *  theme's generic divider, which is not one of the five permitted colours. */
const CHART_RULE_LIGHT = '#E6E2D8';
const CHART_RULE_DARK = '#2B6068';

/** The theme slice the option builders need, with none of the MUI surface area. */
function echartsTheme(theme: Theme, config: LensCardConfig | undefined): LensEChartsTheme {
  const isDark = theme.palette.mode === 'dark';
  return {
    isDark,
    palette: seriesColors(isDark, config?.colors),
    text: theme.palette.text.primary,
    // Axis labels, legend and footer captions read in Ocean Teal on a light
    // field and Compass White on a dark one, not the admin theme's generic
    // secondary text colour, which is not one of the five permitted colours.
    textMuted: isDark ? BRAND_COLORS.compassWhite : BRAND_COLORS.oceanTeal,
    grid: isDark ? CHART_RULE_DARK : CHART_RULE_LIGHT,
    axis: isDark ? CHART_RULE_DARK : CHART_RULE_LIGHT,
    surface: theme.palette.background.paper,
    fontFamily: String(theme.typography.fontFamily ?? 'inherit'),
    // Coral Green never varies with mode, unlike every other chart colour.
    highlight: BRAND_COLORS.coralGreen,
  };
}

/** How a plot names its own form to a screen reader. */
const CHART_NOUN: Partial<Record<LensCard['type'], string>> = {
  bar: 'Bar chart',
  stacked: 'Stacked bar chart',
  line: 'Line chart',
  area: 'Area chart',
  combo: 'Combination chart',
  pie: 'Pie chart',
  scatter: 'Scatter plot',
  heatmap: 'Heatmap',
  boxplot: 'Box plot',
};

/** Why a card of this type could not be drawn from the columns it was given. */
const CANNOT_DRAW: Partial<Record<LensCard['type'], string>> = {
  pie: 'A pie needs a category and a measure that adds up to something.',
  scatter: 'A scatter needs two numeric columns to plot against each other.',
  heatmap: 'A heatmap needs two category columns and a measure.',
  boxplot: 'A box plot needs a category column and a numeric column to spread.',
};

/**
 * Every chart card, drawn through one ECharts instance.
 *
 * Split out of LensCardView so the option can be memoised: the card re-renders
 * on any dashboard state change, and rebuilding then re-applying an option
 * with notMerge on each of those would redraw the plot for reasons that have
 * nothing to do with its data.
 */
export default function LensChartBody({
  card,
  shaped,
  comparison,
  plotHeight,
  selection,
  onSelect,
  theme,
  truncated,
  chartRef,
  onReorderCategories,
  unfilteredShaped,
}: Props) {
  const { config, type } = card;
  const chartConfig = useMemo(() => cardChartConfig({ config, type, view: card.view }), [config, type, card.view]);
  const labelFormat = config?.labelFormat;
  const seriesColumn = card.view?.series;
  const labelColumn = card.view?.x;
  const [highlightedSeries, setHighlightedSeries] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [bodyWidth, setBodyWidth] = useState<number | undefined>();

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const report = () => setBodyWidth(Math.round(node.getBoundingClientRect().width));
    report();
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // The card's own full series list, from its rows before ranges and
  // cross-filter selections narrow them. Only the series names are read off
  // this: it exists purely to give buildChartModel a stable universe to rank
  // colour slots against, never to draw anything itself.
  //
  // unfilteredShaped is already view-shaped by the caller (LensCardView),
  // restricted to exactly this card's own x, series split and measures, the
  // same way `shaped` is: never the raw columns of a shared dataset, which
  // would read every declared measure as a separate series regardless of how
  // many this particular card actually plots.
  const allSeriesNames = useMemo(() => {
    if (!unfilteredShaped || type === 'pie' || type === 'scatter' || type === 'heatmap' || type === 'boxplot') {
      return undefined;
    }
    const universe = buildChartModel(unfilteredShaped.rows, unfilteredShaped.columns, chartConfig)?.series;
    // A card with no more than one series in its own full, unfiltered data
    // has nothing to rank: it is always Ocean Teal at slot 0, whatever a
    // universe would otherwise say (belt and braces on top of buildColorIndex's
    // own >1 guard). Stable cross-narrowing ranking only matters once a card
    // genuinely shows more than one series.
    return universe && universe.length > 1 ? universe : undefined;
  }, [unfilteredShaped, type, chartConfig]);

  const currentModel = useMemo(() => {
    if (type === 'pie' || type === 'scatter' || type === 'heatmap' || type === 'boxplot') {
      return null;
    }
    return buildChartModel(shaped.rows, shaped.columns, chartConfig, undefined, allSeriesNames);
  }, [type, shaped.rows, shaped.columns, chartConfig, allSeriesNames]);

  const pairedChart = useMemo(() => {
    if (!currentModel || !comparison || truncated || (type !== 'line' && type !== 'area')) {
      return null;
    }
    return dailyComparisonChartModel(
      currentModel,
      buildChartModel(comparison.shaped.rows, comparison.shaped.columns, chartConfig),
      comparison.period,
      config,
    );
  }, [currentModel, comparison, truncated, type, chartConfig, config]);
  const model = pairedChart?.model ?? currentModel;

  const interactiveSeries = Boolean(seriesColumn && model && (type === 'line' || type === 'area'));
  const activeHighlight =
    interactiveSeries && model?.series.includes(highlightedSeries ?? '') ? highlightedSeries : null;
  const chartHeight = Math.max(1, plotHeight);

  useEffect(() => setHighlightedSeries(null), [card.id, seriesColumn]);

  const pie = useMemo(
    () => (type === 'pie' ? buildPieModel(shaped.rows, shaped.columns, card.view) : null),
    [type, shaped.rows, shaped.columns, card.view],
  );

  // A scatter is one mark per row against two measures, so it is built from the
  // rows rather than through the series model the axis charts share.
  const scatter = useMemo(() => {
    if (type !== 'scatter') {
      return null;
    }
    const numeric = shaped.columns.filter((column) => shaped.rows.some((row) => isNumeric(row[column])));
    const xKey = card.view?.x && numeric.includes(card.view.x) ? card.view.x : numeric[0];
    const yKey =
      card.view?.value && card.view.value !== xKey ? card.view.value : numeric.find((column) => column !== xKey);
    if (!xKey || !yKey) {
      return null;
    }
    const points = shaped.rows.map((row) => ({ ...row, [xKey]: Number(row[xKey]), [yKey]: Number(row[yKey]) }));
    if (!seriesColumn) {
      return { xKey, yKey, groups: [{ name: displayLensFieldName(yKey, config), rows: points }] };
    }
    const byGroup = new Map<string, Array<Record<string, unknown>>>();
    for (const row of points) {
      const name = String(row[seriesColumn] ?? NONE_LABEL);
      byGroup.set(name, [...(byGroup.get(name) ?? []), row]);
    }
    return { xKey, yKey, groups: [...byGroup.entries()].map(([name, rows]) => ({ name, rows })) };
  }, [type, shaped.rows, shaped.columns, card.view, seriesColumn]);

  const matrix = useMemo(() => {
    if (type !== 'heatmap') {
      return null;
    }
    const [first, second, third] = shaped.columns;
    const xColumn = config?.xColumn ?? first;
    const yColumn = config?.yColumn ?? second;
    const valueColumn: string | undefined =
      config?.valueColumn ?? shaped.columns.find((column) => isNumeric(shaped.rows[0]?.[column])) ?? third;
    if (!xColumn || !yColumn || !valueColumn || xColumn === yColumn) {
      return null;
    }
    return buildMatrixModel(shaped.rows, xColumn, yColumn, valueColumn);
  }, [type, shaped.rows, shaped.columns, config]);

  const distributions = useMemo(() => {
    if (type !== 'boxplot') {
      return null;
    }
    const categoryColumn = config?.categoryColumn ?? config?.xColumn ?? shaped.columns[0];
    const valueColumn: string | undefined =
      config?.valueColumn ??
      shaped.columns.find((column) => column !== categoryColumn && isNumeric(shaped.rows[0]?.[column]));
    if (!categoryColumn || !valueColumn) {
      return null;
    }
    const built = buildDistributions(shaped.rows, categoryColumn, valueColumn);
    return built.length ? built : null;
  }, [type, shaped.rows, shaped.columns, config]);

  const calendar = useContext(LensCalendarContext);
  const timeAxis = useMemo(() => {
    if (!model || (type !== 'line' && type !== 'area')) {
      return { axis: null, partialIndex: -1 };
    }
    const axis = prepareLensTimeAxis(model.rows, model.xKey, calendar);
    // Only the trailing row can be a partial, still-accumulating period: an
    // incomplete day mid-series would mean missing data, not one in progress.
    return { axis, partialIndex: partialPeriodIndex(axis.rows, axis.scale) };
  }, [model, type, calendar]);

  // The exact column and values an active cross-filter selection would need
  // to match this chart's own marks, worked out the same way a click on one
  // sets it. Feeds the option below so the mark that set the selection reads
  // as selected instead of vanishing along with everything that did not.
  //
  // Read on every axis-chart type, not just bar and stacked: a selection made
  // by clicking a bar elsewhere on the dashboard should still recolour the
  // matching series Coral Green on a line, area or combo card here, even
  // though those types cannot originate a click-to-select themselves (that
  // gate is separate, below, and unchanged).
  const crossFilter: LensEChartsCrossFilter | null = useMemo(() => {
    if (!model || !CROSS_FILTER_HIGHLIGHT_TYPES.includes(type)) {
      return null;
    }
    const bySeries = Boolean(seriesColumn) && model.series.length > 1;
    const column = bySeries ? (seriesColumn as string) : (labelColumn ?? model.xKey);
    const values = selection?.[column];
    return values?.length ? { column, values, bySeries } : null;
  }, [model, type, seriesColumn, labelColumn, selection]);

  // The renderer, click handler, keyboard layer and drag gesture must all use
  // this same row set. Looking a rendered dataIndex up in the uncapped model
  // can filter a different category once the top ten has been ranked.
  const displayedRows = useMemo(
    () => (model && (type === 'bar' || type === 'stacked') ? rankedCategoryRows(model, config) : (model?.rows ?? [])),
    [model, type, config],
  );

  const option: LensEChartsOption | null = useMemo(() => {
    const chartTheme = echartsTheme(theme, config);

    if (type === 'pie') {
      return pie ? buildLensEChartsPieOption({ pie, theme: chartTheme, config }) : null;
    }
    if (type === 'scatter') {
      return scatter ? buildLensEChartsScatterOption({ scatter, theme: chartTheme, config }) : null;
    }
    if (type === 'heatmap') {
      return matrix
        ? buildLensEChartsHeatmapOption({
            matrix,
            theme: chartTheme,
            ramp: heatRamp(chartTheme.isDark),
            valueTitle: config?.valueColumn ? displayLensFieldName(config.valueColumn, config) : 'Value',
            format: config?.format,
            xAxis: config?.xAxis,
          })
        : null;
    }
    if (type === 'boxplot') {
      return distributions
        ? buildLensEChartsBoxplotOption({
            distributions,
            theme: chartTheme,
            valueFormat: config?.format,
            max: distributionExtent(distributions).max,
          })
        : null;
    }
    if (!model) {
      return null;
    }

    return buildLensEChartsOption({
      model,
      type,
      config: pairedChart?.config ?? config,
      labelFormat,
      theme: chartTheme,
      timeAxis: timeAxis.axis,
      partialIndex: timeAxis.partialIndex,
      directLabels: interactiveSeries || shouldShowDirectLabels(model.series.length),
      interactiveSeries,
      // A short card's body cannot spare the room the slider and its caption
      // need without squeezing the plot itself to nothing, so the control is
      // withheld there rather than fitted anyway.
      zoom: (timeAxis.axis?.rows ?? model.rows).length > ZOOM_THRESHOLD && canFitZoomControl(chartHeight),
      bodyHeight: chartHeight,
      bodyWidth,
      crossFilter,
      calendar,
    });
  }, [
    type,
    model,
    pie,
    scatter,
    matrix,
    distributions,
    config,
    pairedChart,
    labelFormat,
    theme,
    timeAxis,
    crossFilter,
    chartHeight,
    bodyWidth,
    interactiveSeries,
    calendar,
  ]);

  // The option a card actually drew, published for a driver to read back.
  //
  // ECharts renders to canvas here, so an axis floor, a printed value or a text
  // scale leaves no DOM node to assert against. The built option is the only
  // place those choices are observable from outside React, and it is withheld
  // from a production bundle because nothing in the product reads it.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
      return undefined;
    }
    const host = window as unknown as { __lensChartOptions?: Record<string, unknown> };
    host.__lensChartOptions = { ...(host.__lensChartOptions ?? {}), [card.id]: option };
    return () => {
      if (host.__lensChartOptions) {
        delete host.__lensChartOptions[card.id];
      }
    };
  }, [card.id, option]);

  // Which values this card can raise a cross-filter on. Clicking any segment of
  // a legend-shared series filters by that series' identity; otherwise a mark
  // filters by its own category.
  //
  // Only bar and stacked cards offer it, which is where it was before the
  // renderer changed. Filtering to one point of a line is not a question anyone
  // asks, and a year of hourly rows would publish thousands of buttons to the
  // keyboard layer for no one to tab through.
  const crossFilterKeys: Array<LensCrossFilterKey> = useMemo(() => {
    if (!model || !shaped.rows.length || (type !== 'bar' && type !== 'stacked')) {
      return [];
    }
    const bySeries = Boolean(seriesColumn) && model.series.length > 1;
    const column = bySeries ? (seriesColumn as string) : (labelColumn ?? model.xKey);
    const values = bySeries
      ? model.series
      : [...new Set(displayedRows.map((row) => String(row[model.xKey] ?? '')))].filter(Boolean);
    return values.map((value) => ({
      column,
      value,
      // The value stays raw because it has to match rows, but the label is what
      // a reader sees and has to agree with the axis, which renders a date-like
      // category short. Otherwise the axis says 31 Jul and the chip says
      // 2026-07-31 00:00:00 for the same bar.
      label: `${displayLensFieldName(column, config)}: ${formatLensDateCategoryLabel(value, calendar, column)}`,
      active: Boolean(selection?.[column]?.includes(value)),
    }));
  }, [model, shaped.rows.length, seriesColumn, labelColumn, selection, type, config, displayedRows, calendar]);

  // Only a category axis can be reordered by hand: on a time axis the points
  // are placed by their value, so moving one in the array moves nothing.
  const handleCategoryDrag = useCallback(
    (from: number, to: number) => {
      if (!model || !onReorderCategories || timeAxis.axis?.isTimeAxis) {
        return;
      }
      const current = displayedRows.map((row) => String(row[model.xKey] ?? ''));
      if (from < 0 || from >= current.length || to < 0 || to >= current.length) {
        return;
      }
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      onReorderCategories(next);
    },
    [model, displayedRows, onReorderCategories, timeAxis.axis],
  );

  const handleMarkClick = useCallback(
    (params: LensEChartClick) => {
      if (!model || !shaped.rows.length || (type !== 'bar' && type !== 'stacked')) {
        return;
      }
      const bySeries = Boolean(seriesColumn) && model.series.length > 1;
      if (bySeries) {
        const value = model.series[params.seriesIndex ?? 0];
        if (value) {
          onSelect({ column: seriesColumn as string, value });
        }
        return;
      }
      const row = displayedRows[params.dataIndex ?? -1];
      if (row) {
        onSelect({ column: labelColumn ?? model.xKey, value: String(row[model.xKey] ?? '') });
      }
    },
    [model, displayedRows, shaped.rows.length, seriesColumn, labelColumn, onSelect, type],
  );

  const seriesValue = (params: LensEChartClick): string | undefined => {
    const series = Array.isArray(option?.series) ? option.series[params.seriesIndex ?? -1] : undefined;
    return model?.series.find(
      (value) => series?.id === `lens-series:${value}` || series?.id === `lens-partial:${value}`,
    );
  };

  const valuesForSeries = (value: string): Array<string> => {
    if (!seriesColumn || !shaped.columns.includes(seriesColumn)) return [value];
    const folded = value === OTHER_SERIES && Boolean(model?.foldedCount);
    return [
      ...new Set(
        shaped.rows
          .filter((row) => {
            const name = String(row[seriesColumn] ?? '(null)');
            return name === value || (folded && !model?.series.includes(name));
          })
          .map((row) => String(row[seriesColumn] ?? '')),
      ),
    ];
  };

  const isSeriesFiltered = (value: string): boolean => {
    const values = valuesForSeries(value);
    const selected = seriesColumn ? selection?.[seriesColumn] : undefined;
    return selected?.length === values.length && values.every((name) => selected.includes(name));
  };

  const filterSeries = (value: string) => {
    if (seriesColumn && !isSeriesFiltered(value)) {
      onSelect({ column: seriesColumn, value, replaceValues: valuesForSeries(value) });
    }
  };

  if (!option) {
    return (
      <Typography color="text.secondary" variant="body2">
        {CANNOT_DRAW[type] ?? 'This query does not have the columns this chart needs.'}
      </Typography>
    );
  }

  const partialNote = timeAxis.partialIndex >= 0;

  const measure =
    model?.series.map((name) => displayLensFieldName(name, config)).join(', ') ??
    (type === 'pie'
      ? pie?.slices.map((slice) => slice.name).join(', ')
      : type === 'scatter'
        ? scatter &&
          `${displayLensFieldName(scatter.yKey, config)} against ${displayLensFieldName(scatter.xKey, config)}`
        : type === 'heatmap'
          ? matrix && `${matrix.yValues.length} by ${matrix.xValues.length} cells`
          : distributions?.map((entry) => entry.category).join(', ')) ??
    '';

  return (
    <Box
      ref={containerRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && activeHighlight !== null) {
          event.stopPropagation();
          setHighlightedSeries(null);
        }
      }}
      sx={{
        color: theme.palette.text.secondary,
        fontFamily: theme.typography.fontFamily,
        fontVariantNumeric: 'tabular-nums',
        height: plotHeight,
        boxSizing: 'border-box',
        position: 'relative',
        width: '100%',
      }}
    >
      {partialNote && !truncated && (
        <Typography
          color="text.secondary"
          sx={{
            fontStyle: 'italic',
            position: 'absolute',
            right: 4,
            top: 0,
            zIndex: 1,
          }}
          variant="caption"
        >
          {timeAxis.axis?.scale && partialPeriodNote(timeAxis.axis.scale.unit)}
        </Typography>
      )}
      <LensEChart
        ref={chartRef}
        ariaLabel={`${card.title}. ${CHART_NOUN[type] ?? displayLensFieldName(type, config)}${measure ? ` of ${measure}` : ''}.`}
        height={chartHeight}
        highlightedSeries={interactiveSeries ? activeHighlight : undefined}
        onCategoryDrag={onReorderCategories && !timeAxis.axis?.isTimeAxis ? handleCategoryDrag : undefined}
        onMarkClick={
          interactiveSeries
            ? (params) => {
                const value = seriesValue(params);
                if (value !== undefined) setHighlightedSeries((prev) => (prev === value ? null : value));
              }
            : crossFilterKeys.length
              ? handleMarkClick
              : undefined
        }
        onMarkDoubleClick={
          interactiveSeries
            ? (params) => {
                const value = seriesValue(params);
                if (value !== undefined) filterSeries(value);
              }
            : undefined
        }
        onClearHighlight={interactiveSeries ? () => setHighlightedSeries(null) : undefined}
        option={option}
      />
      {interactiveSeries && model && (
        <LensCrossFilterKeys
          ariaLabel="Highlight a series in this chart"
          keys={model.series.map((value) => ({
            column: seriesColumn as string,
            value,
            label: `Highlight ${displayLensFieldName(value, config)}`,
            active: activeHighlight === value,
          }))}
          onSelect={(key) => setHighlightedSeries(key.value)}
        />
      )}
      <LensCrossFilterKeys
        keys={crossFilterKeys}
        onSelect={(key) => onSelect({ column: key.column, value: key.value })}
      />
    </Box>
  );
}
